import { formatPrice, getProductDisplayName } from './product';

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);

export const getCartProductId = product => {
  if (!isRecord(product)) return null;
  const id = product._id ?? product.id;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
};

export const getCartStock = product => {
  const value = product?.stock;
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const stock = Number(value);
  return Number.isFinite(stock) && stock >= 0 && stock <= Number.MAX_SAFE_INTEGER ? Math.floor(stock) : null;
};

export const getCartPrice = product => {
  const value = product?.price;
  if (value === null || value === undefined || value === '' || value === 0 || value === '0') return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const price = Number(value);
  return Number.isFinite(price) && price > 0 && price <= Number.MAX_SAFE_INTEGER ? price : null;
};

const hasInvalidPrice = product => {
  const value = product.price;
  // An unset or zero price is the store's normal "Consultar" state.
  if (value === null || value === undefined || (typeof value === 'string' && (!value.trim() || value.trim().toLowerCase() === 'consultar'))) return false;
  if ((typeof value === 'number' || typeof value === 'string') && Number(value) === 0) return false;
  return getCartPrice(product) === null;
};

const snapshotProduct = product => {
  const id = getCartProductId(product);
  if (!id) return null;
  const text = value => typeof value === 'string' ? value : '';
  return {
    _id: id,
    artist: text(product.artist), title: text(product.title), name: text(product.name),
    image: text(product.image),
    price: product.price === null || product.price === undefined ? null
      : typeof product.price === 'string' || typeof product.price === 'number' ? product.price : 'invalid',
    stock: getCartStock(product),
    publicationStatus: product.publicationStatus === 'draft' ? 'draft' : 'published',
  };
};

export const restoreCart = data => {
  if (!Array.isArray(data)) return [];
  const items = new Map();
  data.forEach(item => {
    if (!isRecord(item)) return;
    const product = snapshotProduct(item.product);
    if (!product) return;
    const rawQuantity = typeof item.quantity === 'number' || typeof item.quantity === 'string' ? Number(item.quantity) : NaN;
    let quantity = Number.isSafeInteger(rawQuantity) && rawQuantity > 0 ? rawQuantity : 1;
    const previous = items.get(product._id);
    if (previous) quantity = Math.min(Number.MAX_SAFE_INTEGER, previous.quantity + quantity);
    if (product.stock > 0) quantity = Math.min(quantity, product.stock);
    items.set(product._id, { product, quantity });
  });
  return [...items.values()];
};

export const reconcileCart = (cart, catalog) => {
  const products = new Map(catalog.map(product => [getCartProductId(product), product]));
  return cart.map(item => {
    const current = products.get(item.product._id);
    const product = snapshotProduct(current);
    let issue = null;
    if (!product || product.publicationStatus === 'draft') issue = 'unavailable';
    else if (product.stock === null || product.stock <= 0) issue = 'stock';
    else if (!Number.isSafeInteger(item.quantity) || item.quantity <= 0) issue = 'quantity';
    else if (item.quantity > product.stock) issue = 'quantityExceeded';
    else if (hasInvalidPrice(product)) issue = 'price';
    return { product: product || item.product, quantity: item.quantity, issue };
  });
};

export const CART_ISSUE_MESSAGES = {
  unavailable: 'Este disco ya no está disponible. Quitalo de la selección para continuar.',
  stock: 'Este disco está sin stock. Quitalo de la selección para continuar.',
  quantity: 'La cantidad no es válida. Ajustala para continuar.',
  quantityExceeded: 'La cantidad supera el stock disponible. Ajustala para continuar.',
  price: 'Este disco necesita revisión de precio. Quitalo de la selección o consultanos por WhatsApp.',
};

export const getCartSummary = cart => {
  let total = 0;
  let pricedItems = 0;
  let consultItems = 0;
  cart.forEach(item => {
    if (item.issue) return;
    const price = getCartPrice(item.product);
    if (price === null) consultItems += 1;
    else { total += price * item.quantity; pricedItems += 1; }
  });
  const label = pricedItems ? `$${formatPrice(total)}${consultItems ? ' + productos a consultar' : ''}`
    : consultItems ? 'Productos a consultar' : '—';
  return { total, consultItems, label };
};

export const buildCartWhatsAppMessage = cart => {
  const lines = ['Hola DiscoxDiscos, quiero consultar por esta selección:', ''];
  cart.forEach((item, index) => {
    const price = getCartPrice(item.product);
    lines.push(`${index + 1}. ${getProductDisplayName(item.product)}`);
    lines.push(`   Precio: ${price === null ? 'Consultar' : `$${formatPrice(price)}`}`);
    lines.push(`   Cantidad: ${item.quantity}`);
    lines.push('');
  });
  lines.push(`Total seleccion: ${getCartSummary(cart).label}`);
  lines.push('', 'Gracias!');
  return lines.join('\n');
};
